import { Input } from '@insify/design-system/components';
import clsx from 'clsx';
import { get } from 'lodash';
import { useState } from 'react';

import { AddressAutocomplete } from '../AddressAutocomplete';

import styles from './AddressBlock.module.css';

import type { Address } from '../Address/types';
import type { RequestAddressDetail, RequestAutocomplete } from '@insify/frontend-google-maps';
import type { FieldError, FieldErrors, FieldValues, Path, RegisterOptions, UseFormReturn } from 'react-hook-form';

export type PropsAddressBlock<FormValues extends FieldValues> = {
  className?: string;
  requestAutocomplete: RequestAutocomplete;
  requestAddressDetails: RequestAddressDetail;
  onAddressReset?: () => void;
  positionSuggestions?: 'top' | 'bottom';
  addressBasePath?: string;
  shouldShowStreetAddition?: boolean;
  error?: FieldError;
  translations: {
    addressSearchLabel: string;
    addressSearchPlaceholder: string;
    addressSearchError?: string;
    houseNumberLabel: string;
    streetLabel: string;
    houseNumberAdditionLabel?: string;
    houseNumberAdditionPlaceholder?: string;
    optional?: string;
    postalCodeLabel: string;
    cityLabel: string;
  };
  validations?: Partial<Record<keyof Address, RegisterOptions>>;
  useFormReturn: Pick<UseFormReturn<FormValues>, 'formState' | 'getValues' | 'register' | 'resetField'>;
};

const Error: React.FC<{ error: FieldError }> = ({ error }) => (
  <div className={clsx(styles.errorMessage, { [styles[error.type]]: true })}>{error.message}</div>
);

const AddressBlock = <FormValues extends FieldValues>({
  className,
  onAddressReset,
  shouldShowStreetAddition = false,
  error,
  translations: t,
  requestAddressDetails,
  requestAutocomplete,
  positionSuggestions,
  validations,
  useFormReturn,
  addressBasePath = 'address',
}: PropsAddressBlock<FormValues>) => {
  const { formState, getValues, register, resetField } = useFormReturn;
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [shouldPostalCodeEditable, setShouldPostalCodeEditable] = useState(false);

  const pathStreet = `${addressBasePath}.street` as Path<FormValues>;
  const pathHouseNumber = `${addressBasePath}.houseNumber` as Path<FormValues>;
  const pathHouseNumberAddition = `${addressBasePath}.houseNumberAddition` as Path<FormValues>;
  const pathPostalCode = `${addressBasePath}.postalCode` as Path<FormValues>;
  const pathCity = `${addressBasePath}.city` as Path<FormValues>;
  const pathGeoLocation = `${addressBasePath}.geoLocation` as Path<FormValues>;

  const getErrorMessage = <Errors extends FieldErrors<FormValues>>(path: string, errors: Errors) =>
    get(errors, path)?.message;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setIsAddressSelected(false);
    setShouldPostalCodeEditable(false);
    resetField(pathStreet, { defaultValue: '' });
    resetField(pathHouseNumber, { defaultValue: '' });
    resetField(pathHouseNumberAddition, { defaultValue: '' });
    resetField(pathPostalCode, { defaultValue: '' });
    resetField(pathCity, { defaultValue: '' });
    resetField(pathGeoLocation, { defaultValue: { latitude: '', longitude: '' } });
  };

  const handleSuggestionSelect = async ({
    postalCode,
    street,
    houseNumber,
    houseNumberAddition,
    city,
    geoLocation,
  }: Partial<Address>) => {
    if (postalCode === '' || postalCode === undefined) {
      setShouldPostalCodeEditable(true);
    }
    setIsAddressSelected(true);
    onAddressReset?.();

    resetField(pathStreet, { defaultValue: street });
    resetField(pathHouseNumber, { defaultValue: houseNumber });
    resetField(pathHouseNumberAddition, { defaultValue: houseNumberAddition });
    resetField(pathPostalCode, { defaultValue: postalCode });
    resetField(pathCity, { defaultValue: city });
    resetField(pathGeoLocation, { defaultValue: geoLocation });

    // Make sure our custom form triggers an onchange to update a command
    void registerHouseNumber.onChange({ target: { value: houseNumber } });
  };

  const streetValue = getValues(pathStreet);
  const hasAddress = streetValue !== undefined && streetValue !== '';
  const shouldShowDetailFields = hasAddress || isAddressSelected;

  const streetErrorMessage = getErrorMessage(pathStreet, formState.errors);

  const autoCompleteError =
    !shouldShowDetailFields && streetErrorMessage !== undefined ? t.addressSearchError : undefined;

  const registerHouseNumber = register(pathHouseNumber, { shouldUnregister: true, ...validations?.houseNumber });

  return (
    <div className={className}>
      <AddressAutocomplete
        requestAutocomplete={requestAutocomplete}
        requestAddressDetail={requestAddressDetails}
        positionSuggestions={positionSuggestions}
        formatAutoCompleteSuggestion={(address) => address.label}
        label={t.addressSearchLabel}
        placeholder={t.addressSearchPlaceholder}
        onChange={handleChange}
        onSelect={handleSuggestionSelect}
        error={autoCompleteError}
        areFieldsVisible={shouldShowDetailFields}
        fields={
          <>
            <div className={styles.detailFieldsRow}>
              <Input
                {...registerHouseNumber}
                trackingId='housenumber'
                id='housenumber'
                className={styles.detailFieldNarrowerCell}
                autoComplete='address-line2'
                error={getErrorMessage(pathHouseNumber, formState.errors)}
                label={t.houseNumberLabel}
              />
              <Input
                {...register(pathStreet, { shouldUnregister: false, ...validations?.street })}
                trackingId='street'
                id='street'
                className={styles.detailFieldWiderCell}
                autoComplete='address-line1'
                disabled
                error={getErrorMessage(pathStreet, formState.errors)}
                label={t.streetLabel}
              />
            </div>
            {shouldShowStreetAddition && (
              <Input
                {...register(pathHouseNumberAddition)}
                trackingId='streetAddition'
                id='streetaddition'
                autoComplete='address-line3'
                label={t.houseNumberAdditionLabel}
                placeholder={t.houseNumberAdditionPlaceholder}
                topHint={t.optional}
              />
            )}
            <div className={styles.detailFieldsRow}>
              <Input
                {...register(pathPostalCode, validations?.postalCode)}
                trackingId='postalcode'
                id='postalcode'
                className={styles.detailFieldNarrowerCell}
                autoComplete='postal-code'
                disabled={!shouldPostalCodeEditable}
                error={getErrorMessage(pathPostalCode, formState.errors)}
                label={t.postalCodeLabel}
              />
              <Input
                {...register(pathCity, validations?.city)}
                trackingId='city'
                id='city'
                className={styles.detailFieldWiderCell}
                autoComplete='address-level2'
                disabled
                error={getErrorMessage(pathCity, formState.errors)}
                label={t.cityLabel}
              />
            </div>
            <input {...register(pathGeoLocation)} type='hidden' />
            {error !== undefined && <Error error={error} />}
          </>
        }
      />
    </div>
  );
};

export { AddressBlock };
